import { type FC, memo, useCallback, useMemo } from "react";
import type { EventType } from "src/http/types";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { DateType, type Dates } from "src/types/shared/cookies";
import { MobileCalendarDateSelector } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/mobile-calendar-filter-row/MobileCalendarDateSelector";
import { MobileCalendarEventTypeSelector } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/mobile-calendar-filter-row/MobileCalendarEventTypeSelector";
import { useDateCookieOptions, withSetDatesCookie } from "src/website-apps/shared/context/date-context";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { filterBarContainer, filterBarRow } from "./styles";

interface Props {
  filterTypeOptions?: readonly EventType[];
}

export const FilterBar: FC<Props> = memo(({ filterTypeOptions = [] }) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const storePageEvent = useStorePageEvent();
  const [state, dispatch, { setGridFilterType, setDates }] = useEventsGridContext();
  const { gridFilterType, dates } = state.filters;

  const dateCookieOptions = useDateCookieOptions();
  const execWithSetDatesCookie = useMemo(() => ({ withSetDatesCookie, processGetDates: () => dates }), [dates]);
  const currentDates = useMemo(() => execWithSetDatesCookie.processGetDates(), [execWithSetDatesCookie]);

  const onDatesChange = useCallback(
    (val: Dates) => {
      const setWithCookie = execWithSetDatesCookie.withSetDatesCookie(setDates, env, dateCookieOptions);
      setWithCookie(dispatch, state, val);
    },
    [env, execWithSetDatesCookie, setDates, dispatch, state, dateCookieOptions],
  );

  const onEventTypeChange = useCallback(
    (val: EventType) => {
      setGridFilterType(dispatch, state, val);
      storePageEvent(
        PE_TYPE.CLICK,
        PE_KEYS.RESIDENCY_CALENDAR_EVENT_TYPE_FILTER_CLICK,
        String(filterTypeOptions.indexOf(val)),
      );
    },
    [setGridFilterType, dispatch, state, filterTypeOptions, storePageEvent],
  );

  return (
    <div className={filterBarContainer}>
      <div className={filterBarRow}>
        <MobileCalendarDateSelector
          addActiveState={currentDates?.type !== DateType.ALL_DATES}
          chipVariant="outline"
          dates={currentDates ?? undefined}
          hideIcon={false}
          onDatesChange={onDatesChange}
        />
        <MobileCalendarEventTypeSelector onEventTypeChange={onEventTypeChange} selectedEventType={gridFilterType} />
      </div>
    </div>
  );
});
FilterBar.displayName = "FilterBar";
